/**
 * Referral landing — `/r/:slug` on app.visagenius.io.
 *
 * Captures the referrer slug from the URL, stashes it in localStorage
 * so it survives the sign-up flow, and forwards the visitor to the
 * regular entry path (Scholar URL step). The sign-up code reads
 * `vg-ref-code` and passes it to the backend as `referral_code`;
 * `_apply_referral_attribution` treats slugs as a valid code shape
 * and credits the referring tenant.
 *
 * The marketing domain (visagenius.io) redirects `/r/*` → this route
 * via `landing/_redirects`, so a colleague clicking the shared link
 * eventually lands here regardless of which domain they hit first.
 */
import { useEffect } from "react";
import { Navigate, useParams } from "react-router-dom";

const STORAGE_KEY = "vg-ref-code";

export function ReferralLanding() {
  const { slug = "" } = useParams<{ slug: string }>();
  useEffect(() => {
    if (!slug) return;
    try {
      // Cap to 64 chars (tenant.slug max length on the backend); strip
      // anything other than the slug shape so a malformed link can't
      // smuggle script content into a downstream form value.
      const cleaned = slug.toLowerCase().replace(/[^a-z0-9-]/g, "").slice(0, 64);
      if (cleaned) localStorage.setItem(STORAGE_KEY, cleaned);
    } catch {
      /* localStorage unavailable — link still works, just no credit */
    }
  }, [slug]);
  return <Navigate to="/scholar-path" replace />;
}

/** Read + forget the stored referral code. Call once per signup attempt
 *  so the code doesn't ride along on every subsequent sign-in. */
export function consumeReferralCode(): string | undefined {
  try {
    const code = localStorage.getItem(STORAGE_KEY);
    if (code) {
      localStorage.removeItem(STORAGE_KEY);
      return code;
    }
  } catch {
    /* localStorage unavailable */
  }
  return undefined;
}

/** Non-destructive read — used when the caller wants to display
 *  "Invited by Anika" badges or similar without consuming the code. */
export function peekReferralCode(): string | undefined {
  try {
    return localStorage.getItem(STORAGE_KEY) || undefined;
  } catch {
    return undefined;
  }
}
